import Email, { BodyContentType } from "@/domain/entities/emails/Email";
import EmailToSend from "@/domain/entities/emails/EmailToSend";
import SmartLabel from "@/domain/enums/SmartLabel";
import { deriveUniqueMailvistaId } from "@/lib/mailIdentity";
import {
    Mail,
    MailSend,
    ReplyForward,
    BodyContentType as SDKBodyContentType,
} from "@cs_teamwaydev/mailvista-sdk_react";
import aiDetailsMapper from "./aiDetailsMapper";
import { attachmentMapper, mapToSDKAttachment } from "./attachementMapper";
import emailUserMapper from "./emailUserMapper";
import eventDetailMapper from "./eventDetailMapper";
import linkObjectMapper from "./linkObjectMapper";

export const emailMapper = (mail: Mail): Email => {
    const uniqueMailvistaId =
        mail.uniqueMailvistaId ?? deriveUniqueMailvistaId(mail);

    return new Email({
        id: mail.id,
        internetMessageId: mail.internetMessageId,
        uniqueMailvistaId,
        parentFolderId: mail.parentFolderId,
        subject: mail.subject,
        message: mail.message,
        messagePreview: mail.messagePreview,
        isRead: mail.isRead,
        from: emailUserMapper(mail.from),
        to: mail.to.map(emailUserMapper),
        cc: mail.cc.map(emailUserMapper),
        bcc: mail.bcc.map(emailUserMapper),
        // Pas de valeur par défaut : un label inventé ferait évincer le mail de sa liste.
        // Un label inconnu du front retombe donc aussi sur `undefined` — mieux vaut garder
        // le mail affiché que le déplacer sur une valeur qu'on ne sait pas interpréter.
        smartLabel: Object.values(SmartLabel).includes(
            mail.smartLabel as SmartLabel,
        )
            ? (mail.smartLabel as SmartLabel)
            : undefined,
        isFocus: mail.isFocus,
        isStarred: mail.isStarred,
        isImportant: mail.isImportant,
        sentDateTime: new Date(mail.sentDateTime || new Date()),
        attachments: mail.attachments.map((attachment) =>
            attachmentMapper(attachment, uniqueMailvistaId),
        ),
        isUserProcessed: mail.isUserProcessed,
        aiDetails: mail.aiDetails
            ? aiDetailsMapper(mail.aiDetails, mail.internetMessageId)
            : undefined,
        componentSize: mail.componentSize,
        linkedEventDetails: mail.linkedEventDetails
            ? eventDetailMapper(
                  new Date(mail.sentDateTime || new Date()),
                  mail.linkedEventDetails,
              )
            : undefined,
        uniqueBody: mail.uniqueBody,
        conversationId: mail.conversationId,
        links: mail.links.map(linkObjectMapper),
        hasAttachment: mail.hasAttachment,
        bodyContentType: mapBodyContentType(mail.bodyContentType),
        uniqueBodyContentType: mapBodyContentType(mail.uniqueBodyContentType),
    });
};

export const emailToSendToSendMailSDKMapper = (
    mail: EmailToSend,
): MailSend => ({
    subject: mail.subject || "",
    content: mail.content,
    toRecipients: mail.toRecipients,
    ccRecipients: mail.ccRecipients,
    bccRecipients: mail.bccRecipients,
    attachments: mail.attachments?.map(mapToSDKAttachment),
    contentType: mail.contentType,
});

export const emailToSendToForwardMailSDKMapper = (
    mail: EmailToSend,
): ReplyForward => ({
    content: mail.content,
    toRecipients: mail.toRecipients,
    ccRecipients: mail.ccRecipients,
    bccRecipients: mail.bccRecipients,
    attachments: mail.attachments?.map(mapToSDKAttachment),
    contentType: mail.contentType,
    subject: mail.subject,
});

const mapBodyContentType = (
    contentType: SDKBodyContentType | undefined,
): BodyContentType =>
    contentType === SDKBodyContentType.TEXT
        ? BodyContentType.TEXT
        : BodyContentType.HTML;
